import { Add, Close, Work } from '@mui/icons-material';
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  ButtonGroup,
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import { Field } from 'formik';
import React, { FC, Fragment } from 'react';
// Components
import { Card, SMBSelect } from '../../../components';
import { CheckListWorkItem } from './ChecklistWorkItem';
import { IChecklistTemplateWorkItemCategory, ICatalog } from '../../../models';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { useSnackbar } from 'notistack';

interface IChecklistTemplatesWorkItems {
  currentWorkItemIndex: number | null;
  setFieldValue: (key: string, val: any) => void;
  values: any;
  setCurrentWorkItemIndex: (val: number | null) => void;
  isLoadingCategories: boolean;
  isLoadingCatalogs: boolean;
  workItemCategories: IChecklistTemplateWorkItemCategory[];
  handleBlur: (event: React.FocusEvent<any>) => void;
  hasDuplicateCategory: boolean;
  requirementTypes: any[];
  catalogs: ICatalog[];
  setSelectedCatalogImage: (imageUrl: string) => void;
  selectedCatalogImage: string;
  setModalImageSrc: (src: string) => void;
}

export const ChecklistTemplatesWorkItems: FC<IChecklistTemplatesWorkItems> = ({
  currentWorkItemIndex,
  setFieldValue,
  values,
  setCurrentWorkItemIndex,
  workItemCategories,
  handleBlur,
  hasDuplicateCategory,
  requirementTypes,
  isLoadingCategories,
  selectedCatalogImage,
  isLoadingCatalogs,
  catalogs,
  setSelectedCatalogImage,
  setModalImageSrc
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const workItems = [...values.workItems];
    const [removed] = workItems.splice(result.source.index, 1);
    workItems.splice(result.destination.index, 0, removed);

    setFieldValue('workItems', workItems);
  };
  return (
    <Card icon={<Work />} title="Work Items">
      {currentWorkItemIndex === null && (
        <>
          <Box display="flex" justifyContent="flex-end" marginBottom="1rem">
            <Button
              type="button"
              startIcon={<Add />}
              onClick={() => {
                setFieldValue('workItems', [
                  ...(values.workItems || []),
                  {
                    photoRequirementTypeId: '',
                    noteRequirementTypeId: '',
                    catalogId: '',
                    categoryId: '',
                    otherCategory: '',
                    description: ''
                  }
                ]);
                setCurrentWorkItemIndex(
                  values.workItems.length === 0 ? 0 : values.workItems.length
                );
              }}
              variant="contained"
              color="secondary"
            >
              Add Work Item
            </Button>
          </Box>
          <DragDropContext onDragEnd={result => onDragEnd(result)}>
            <Droppable droppableId="work-item-droppable" direction="vertical">
              {(provided, snapshot) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {values.workItems.map((item, index) => {
                    let selectedCat = workItemCategories?.find(
                      cat => cat?.checklistWorkItemCategoryId === item?.categoryId
                    );
                    if (!selectedCat && item?.otherCategory) {
                      selectedCat = {
                        checklistWorkItemCategoryId: null,
                        name: item?.otherCategory,
                        canDelete: false
                      };
                    }
                    return (
                      <Draggable
                        key={index}
                        draggableId={`work-item-${index}`}
                        index={index}
                        isDragDisabled={!!currentWorkItemIndex}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <CheckListWorkItem
                              itemIndex={index}
                              setFieldValue={setFieldValue}
                              workItems={values.workItems}
                              catagoryName={selectedCat?.name ?? ''}
                              workItem={item}
                              isDragging={snapshot.isDragging}
                              setCurrentWorkItemIndex={setCurrentWorkItemIndex}
                            />
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </>
      )}
      {(currentWorkItemIndex === 0 || currentWorkItemIndex > 0) &&
        values.workItems[currentWorkItemIndex] && (
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <Field name={`workItems.${currentWorkItemIndex}.categoryId`}>
                {({ field }) => {
                  return (
                    <SMBSelect
                      label="Category"
                      variant="outlined"
                      required
                      disabled={isLoadingCategories}
                      name={`workItems.${currentWorkItemIndex}.categoryId`}
                      value={values.workItems[currentWorkItemIndex]?.categoryId || ''}
                      onChange={e => {
                        field.onChange(e);
                        setFieldValue(`workItems.${currentWorkItemIndex}.otherCategory`, '');
                      }}
                      handleClear={() =>
                        setFieldValue(`workItems.${currentWorkItemIndex}.otherCategory`, '')
                      }
                    >
                      {workItemCategories.map((cat: IChecklistTemplateWorkItemCategory, index) => {
                        return (
                          <MenuItem key={`${index}`} value={cat.checklistWorkItemCategoryId}>
                            {cat.name}
                          </MenuItem>
                        );
                      })}
                    </SMBSelect>
                  );
                }}
              </Field>
            </Grid>
            {values.workItems[currentWorkItemIndex].categoryId === 'other' && (
              <Grid item xs={12} sm={6}>
                <Field name={`workItems.${currentWorkItemIndex}.otherCategory`}>
                  {({ field }) => {
                    return (
                      <TextField
                        {...field}
                        fullWidth
                        variant="outlined"
                        autoComplete="nope"
                        label="Other Category"
                        size="small"
                        name={`workItems.${currentWorkItemIndex}.otherCategory`}
                        value={values.workItems[currentWorkItemIndex].otherCategory}
                        onBlur={handleBlur}
                        error={hasDuplicateCategory ? true : false}
                        helperText={hasDuplicateCategory ? 'Category already exists' : ''}
                      />
                    );
                  }}
                </Field>
              </Grid>
            )}
            <Grid item xs={12}>
              <Field name={`workItems.${currentWorkItemIndex}.description`}>
                {({ field }) => {
                  return (
                    <TextField
                      {...field}
                      fullWidth
                      variant="outlined"
                      autoComplete="nope"
                      label="Description"
                      size="small"
                      multiline
                      minRows={2}
                      name={`workItems.${currentWorkItemIndex}.description`}
                      value={values.workItems[currentWorkItemIndex].description}
                      onBlur={handleBlur}
                    />
                  );
                }}
              </Field>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field name={`workItems.${currentWorkItemIndex}.photoRequirementTypeId`}>
                {({ field }) => {
                  return (
                    <FormControl fullWidth required variant="outlined" size="small">
                      <Typography>Photo Requirement*</Typography>
                      <RadioGroup
                        value={values.workItems[currentWorkItemIndex]?.photoRequirementTypeId}
                        onChange={e =>
                          setFieldValue(
                            `workItems.${currentWorkItemIndex}.photoRequirementTypeId`,
                            e.target.value
                          )
                        }
                        aria-label="photo requirement"
                        name={`workItems.${currentWorkItemIndex}.photoRequirementTypeId`}
                      >
                        {requirementTypes.map((type, index) => {
                          return (
                            <Fragment key={`${index}`}>
                              <FormControlLabel
                                value={type.requirementTypeId}
                                control={<Radio />}
                                label={type.name}
                              />
                            </Fragment>
                          );
                        })}
                      </RadioGroup>
                    </FormControl>
                  );
                }}
              </Field>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field name={`workItems.${currentWorkItemIndex}.noteRequirementTypeId`}>
                {({ field }) => {
                  return (
                    <FormControl fullWidth required variant="outlined" size="small">
                      <Typography>Note Requirement*</Typography>
                      <RadioGroup
                        value={values.workItems[currentWorkItemIndex]?.noteRequirementTypeId}
                        onChange={e =>
                          setFieldValue(
                            `workItems.${currentWorkItemIndex}.noteRequirementTypeId`,
                            e.target.value
                          )
                        }
                        aria-label="note requirement"
                        name={`workItems.${currentWorkItemIndex}.noteRequirementTypeId`}
                      >
                        {requirementTypes.map((type, index) => {
                          return (
                            <Fragment key={`${index}`}>
                              <FormControlLabel
                                value={type.requirementTypeId}
                                control={<Radio />}
                                label={type.name}
                              />
                            </Fragment>
                          );
                        })}
                      </RadioGroup>
                    </FormControl>
                  );
                }}
              </Field>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field name={`workItems.${currentWorkItemIndex}.catalogId`}>
                {({ field }) => {
                  return (
                    <SMBSelect
                      {...field}
                      label="Detail Reference"
                      variant="outlined"
                      value={values.workItems[currentWorkItemIndex]?.catalogId ?? ''}
                      disabled={isLoadingCatalogs}
                      onBlur={handleBlur}
                      onChange={e => {
                        const selected = catalogs.find(
                          cat => cat.catalogId === Number(e.target.value)
                        );
                        setSelectedCatalogImage(selected?.imageUrl ?? '');
                        field.onChange(e);
                      }}
                      handleClear={() => {
                        setSelectedCatalogImage('');
                        setFieldValue(`workItems.${currentWorkItemIndex}.catalogId`, '');
                      }}
                    >
                      {catalogs.map((cat: ICatalog, index) => {
                        return (
                          <MenuItem key={`${index}`} value={cat.catalogId}>
                            {cat.name}
                          </MenuItem>
                        );
                      })}
                    </SMBSelect>
                  );
                }}
              </Field>
            </Grid>
            {values.workItems[currentWorkItemIndex]?.catalogId > 0 && (
              <Grid item xs={12}>
                <Typography>Detail Preview</Typography>
                <Box
                  sx={{
                    userSelect: 'none',
                    cursor: 'pointer'
                  }}
                  onClick={() => setModalImageSrc(selectedCatalogImage)}
                >
                  <Box
                    component="img"
                    src={selectedCatalogImage}
                    sx={{
                      cursor: 'pointer',
                      height: 50,
                      width: 50
                    }}
                  />
                </Box>
              </Grid>
            )}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" justifyContent="flex-end">
                <ButtonGroup>
                  <Button
                    color="secondary"
                    variant="contained"
                    startIcon={<Add />}
                    disabled={
                      !values.workItems[currentWorkItemIndex].categoryId ||
                      !values.workItems[currentWorkItemIndex].photoRequirementTypeId ||
                      !values.workItems[currentWorkItemIndex].noteRequirementTypeId ||
                      hasDuplicateCategory
                    }
                    onClick={() => {
                      setCurrentWorkItemIndex(null);
                      enqueueSnackbar(`Work Item Added, be sure to save the checklist template.`, {
                        variant: 'success'
                      });
                    }}
                  >
                    Done
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Close />}
                    color="inherit"
                    onClick={() => {
                      setCurrentWorkItemIndex(null);
                    }}
                  >
                    Cancel
                  </Button>
                </ButtonGroup>
              </Box>
            </Grid>
          </Grid>
        )}
    </Card>
  );
};
